import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchStudentReports } from "../../redux/admin/studentReport.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import {
  clearSingleStudentState,
  setCurrentPage,
} from "../../redux/admin/studentReport.slice";
import {
  selectStudentsSummary,
  selectSelectedStudentProfile,
  selectSummaryStats,
  selectSessions,
  selectReportType,
  selectReportLoading,
  selectReportError,
  selectReportCurrentPage,
  selectReportTotalPages,
  selectReportTotal,
  selectReportLimit,
  selectIsSingleStudentReport,
} from "../../redux/admin/studentReport.selector";
import { apiCall } from "../../api/core/apiCall";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =====================================================
   SEARCHABLE ACTIVE STUDENT COMBOBOX
===================================================== */
const StudentCombobox = ({ students = [], value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const fullName = `${s.name || ""} ${s.last_name || ""}`.toLowerCase();
      const admNo = String(s.admission_number || "").toLowerCase();
      return fullName.includes(q) || admNo.includes(q);
    });
  }, [students, query]);

  useEffect(() => {
    if (!open) {
      setQuery(
        selectedStudent
          ? `${selectedStudent.admission_number} — ${selectedStudent.name} ${selectedStudent.last_name || ""}`.trim()
          : ""
      );
    }
  }, [selectedStudent, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const commitSelection = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
    if (student) {
      setQuery(`${student.admission_number} — ${student.name} ${student.last_name || ""}`.trim());
    } else {
      setQuery("");
    }
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) commitSelection(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(
        selectedStudent
          ? `${selectedStudent.admission_number} — ${selectedStudent.name} ${selectedStudent.last_name || ""}`.trim()
          : ""
      );
    }
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div
        className={`mnc-combobox-field ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
        onClick={() => {
          if (!disabled && !open) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <svg className="mnc-combobox-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>

        <input
          ref={inputRef}
          type="text"
          className="mnc-field mnc-combobox-input fw-600"
          placeholder="Search by student name or admission number…"
          value={query}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            if (selectedStudent) setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />

        <div className="mnc-combobox-controls">
          {value && (
            <button
              type="button"
              className="mnc-combobox-clear"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                commitSelection(null);
              }}
            >
              ×
            </button>
          )}
          <svg
            className={`mnc-combobox-chevron ${open ? "is-open" : ""}`}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {open && (
        <ul className="mnc-combobox-list" ref={listRef} role="listbox">
          <li
            role="option"
            aria-selected={!value}
            className={`mnc-combobox-option mnc-combobox-option-overview ${!value ? "is-selected" : ""}`}
            onClick={() => commitSelection(null)}
          >
            <span className="mnc-combobox-avatar mnc-combobox-avatar-overview">⚡</span>
            <span className="mnc-combobox-option-text">
              <span className="mnc-combobox-option-name text-primary">All Active Students</span>
              <span className="mnc-combobox-option-sub">Aggregate summary across all active students</span>
            </span>
            {!value && (
              <svg className="mnc-combobox-check" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </li>

          <li className="mnc-combobox-divider" aria-hidden="true" />

          {filtered.length === 0 ? (
            <li className="mnc-combobox-empty">No active students match “{query}”</li>
          ) : (
            filtered.map((student, index) => {
              const isSelected = String(student.id) === String(value);
              return (
                <li
                  key={student.id}
                  role="option"
                  aria-selected={isSelected}
                  className={`mnc-combobox-option ${index === activeIndex ? "is-active" : ""} ${isSelected ? "is-selected" : ""}`}
                  onClick={() => commitSelection(student)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="mnc-combobox-avatar">
                    {student.name?.charAt(0)?.toUpperCase() || "S"}
                  </span>
                  <span className="mnc-combobox-option-text">
                    <span className="mnc-combobox-option-name">
                      {student.name} {student.last_name || ""}
                    </span>
                    <span className="mnc-combobox-option-sub">{student.admission_number}</span>
                  </span>
                  {isSelected && (
                    <svg className="mnc-combobox-check" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

/* =====================================================
   MAIN STUDENT REPORTS COMPONENT
===================================================== */
const StudentReports = () => {
  const dispatch = useDispatch();

  // 1. REDUX ACTIVE STUDENTS LIST
  const { students = [] } = useSelector((state) => state.admin?.students || {});

  const activeStudents = useMemo(() => {
    if (!Array.isArray(students)) return [];
    return students.filter((s) => {
      if (!s) return false;
      if (s.status === undefined || s.status === null) return true;
      return (
        s.status === "active" ||
        s.status === 1 ||
        String(s.status).toLowerCase() === "active"
      );
    });
  }, [students]);

  // REDUX REPORT SELECTORS
  const studentsSummary = useSelector(selectStudentsSummary);
  const selectedStudentProfile = useSelector(selectSelectedStudentProfile);
  const summaryStats = useSelector(selectSummaryStats);
  const sessions = useSelector(selectSessions);
  const reportType = useSelector(selectReportType);
  const loading = useSelector(selectReportLoading);
  const error = useSelector(selectReportError);
  const currentPage = useSelector(selectReportCurrentPage);
  const totalPages = useSelector(selectReportTotalPages);
  const total = useSelector(selectReportTotal);
  const limit = useSelector(selectReportLimit);
  const isSingleStudent = useSelector(selectIsSingleStudentReport);

  // Extract Homework list directly from Redux raw report payload
  const homeworkList = useSelector(
    (state) => state.admin?.studentReport?.homework || []
  );

  // FORM & TAB STATE
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState("attendance"); // 'attendance' | 'homework'

  /* =====================================================
     INITIALIZATION: LOAD ACTIVE STUDENTS
  ===================================================== */
  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
  }, [dispatch]);

  /* =====================================================
     TOASTR ERROR NOTIFICATIONS
  ===================================================== */
  useEffect(() => {
    if (error) {
      toastr.error(error);
    }
  }, [error]);

  /* =====================================================
     REPORT GENERATION DISPATCH
  ===================================================== */
  const handleGenerateReport = useCallback(
    (studentIdOverride, pageOverride) => {
      const targetStudentId = studentIdOverride !== undefined ? studentIdOverride : selectedStudentId;
      const pageToFetch = pageOverride || (targetStudentId ? 1 : currentPage);

      dispatch(
        fetchStudentReports({
          studentId: targetStudentId || null,
          fromDate,
          toDate,
          page: pageToFetch,
          limit,
        })
      )
        .unwrap()
        .then(() => {
          toastr.success("Student report generated successfully.");
        })
        .catch((err) => {
          toastr.error(err || "Failed to generate student report.");
        });
    },
    [dispatch, selectedStudentId, fromDate, toDate, currentPage, limit]
  );

  const handleStudentSelect = (studentId) => {
    setSelectedStudentId(studentId);
    if (studentId) {
      handleGenerateReport(studentId, 1);
    } else {
      dispatch(clearSingleStudentState());
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedStudentId) {
      dispatch(setCurrentPage(1));
    }
    handleGenerateReport(selectedStudentId, 1);
  };

  const handleResetFilters = () => {
    setSelectedStudentId("");
    setFromDate("");
    setToDate("");
    dispatch(setCurrentPage(1));
    dispatch(clearSingleStudentState());
    toastr.info("Filters cleared.");
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    dispatch(setCurrentPage(newPage));
    dispatch(
      fetchStudentReports({
        studentId: selectedStudentId || null,
        fromDate,
        toDate,
        page: newPage,
        limit,
      })
    );
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const queryParams = { export_pdf: "1" };
      if (selectedStudentId) queryParams.student_id = selectedStudentId;
      if (fromDate) queryParams.from_date = fromDate;
      if (toDate) queryParams.to_date = toDate;

      const blob = await apiCall(
        "GET",
        "/admin/students/student_reports.php",
        null,
        "Download PDF Report",
        {
          params: queryParams,
          responseType: "blob",
        }
      );

      if (!blob || !(blob instanceof Blob)) {
        toastr.error("Failed to generate PDF file.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const fileLabel =
        isSingleStudent && selectedStudentObj
          ? `Report_${selectedStudentObj.admission_number || "Student"}`
          : "Academic_Summary_Report";

      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastr.success("Report PDF downloaded successfully.");
    } catch (err) {
      toastr.error("Network error: unable to download the report.");
    } finally {
      setDownloading(false);
    }
  };

  const selectedStudentObj = useMemo(
    () => activeStudents.find((s) => String(s.id) === String(selectedStudentId)),
    [activeStudents, selectedStudentId]
  );

  return (
    <div className="mnc-page-bg py-4 py-md-5">
      <div className="container-fluid px-2 px-md-4 max-w-1400">

        {/* HEADER SECTION */}
        <div className="mnc-card shadow-sm border mb-4">
          <div className="mnc-card-header p-3 p-md-4 bg-white border-bottom">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <span className="badge-mnc bg-primary-light text-primary-dark uppercase tracking-wider mb-2 d-inline-block">
                  Academic Analytics &amp; Performance
                </span>
                <h4 className="fw-800 text-dark mb-1">Student Attendance &amp; Homework Reports</h4>
                <p className="text-muted small mb-0">
                  Compile session attendance histories, topic coverage logs, and homework assignment completion rates.
                </p>
              </div>
              <div className="mnc-header-actions d-flex align-items-center gap-2">
                {isSingleStudent && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-700"
                    onClick={handleResetFilters}
                  >
                    ← Clear Selection
                  </button>
                )}
                {reportType && (
                  <button
                    type="button"
                    className="btn btn-sm btn-dark rounded-pill px-3 fw-700 d-inline-flex align-items-center gap-2"
                    onClick={handleDownloadReport}
                    disabled={loading || downloading}
                  >
                    {downloading ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        Preparing PDF...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 3v12"></path>
                          <path d="M7 10l5 5 5-5"></path>
                          <path d="M5 21h14"></path>
                        </svg>
                        Download Report
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* FILTER CONTROLS FORM */}
          <form onSubmit={handleSubmit} className="p-3 p-md-4 bg-white" style={{ position: "relative", zIndex: 10 }}>
            <div className="row g-3">

              {/* 1. ACTIVE STUDENT COMBOBOX */}
              <div className="col-12 col-lg-5">
                <label className="mnc-label">Select Active Student Target *</label>
                <StudentCombobox
                  students={activeStudents}
                  value={selectedStudentId}
                  onSelect={handleStudentSelect}
                  disabled={loading}
                />
                {selectedStudentObj && (
                  <div className="mnc-selected-chip mt-2">
                    <span className="mnc-selected-chip-dot" />
                    {selectedStudentObj.name} {selectedStudentObj.last_name || ""} · {selectedStudentObj.admission_number}
                  </div>
                )}
              </div>

              {/* 2. FROM DATE */}
              <div className="col-6 col-md-3 col-lg-2">
                <label className="mnc-label">From Date</label>
                <input
                  type="date"
                  className="mnc-field fw-600"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* 3. TO DATE */}
              <div className="col-6 col-md-3 col-lg-2">
                <label className="mnc-label">To Date</label>
                <input
                  type="date"
                  className="mnc-field fw-600"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* 4. ACTION BUTTONS */}
              <div className="col-12 col-lg-3 d-flex align-items-end gap-2 mnc-action-buttons">
                <button
                  type="submit"
                  className="btn-mnc-sync flex-grow-1"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Compiling...</>
                  ) : (
                    "Generate Report"
                  )}
                </button>
                {(selectedStudentId || fromDate || toDate) && (
                  <button
                    type="button"
                    className="btn-mnc-discard"
                    onClick={handleResetFilters}
                    disabled={loading}
                  >
                    Reset
                  </button>
                )}
              </div>

            </div>
          </form>
        </div>

        {/* LOADING INDICATOR */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status"></div>
            <p className="fw-700 text-muted">Compiling Academic &amp; Homework Report Data...</p>
          </div>
        )}

        {/* INITIAL PROMPT BEFORE SELECTION */}
        {!loading && !reportType && (
          <div className="mnc-card p-5 text-center bg-white border">
            <div className="mb-3">
              <span className="badge-mnc bg-primary-light text-primary-dark p-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </span>
            </div>
            <h5 className="fw-800 text-dark">No Student Selected</h5>
            <p className="text-muted small mx-auto" style={{ maxWidth: 450 }}>
              Please select an active student from the dropdown above to inspect attendance logs and homework submission histories.
            </p>
          </div>
        )}

        {/* =====================================================
            SCENARIO A: SINGLE STUDENT DETAILED REPORT
        ===================================================== */}
        {!loading && isSingleStudent && (
          <div className="printable-report">

            {/* PROFILE & STATS SUMMARY BANNER */}
            <div className="row g-3 mb-4">
              
              {/* STUDENT PROFILE CARD */}
              <div className="col-12 col-md-4">
                <div className="mnc-card p-3 p-md-4 h-100 border bg-white">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="avatar-circle bg-primary-light text-primary-dark fw-800">
                      {selectedStudentProfile?.name?.charAt(0) || "S"}
                    </div>
                    <div>
                      <h5 className="fw-800 text-dark mb-0">
                        {selectedStudentProfile?.name} {selectedStudentProfile?.last_name || ""}
                      </h5>
                      <span className="badge bg-light text-dark border mt-1">
                        Adm No: {selectedStudentProfile?.admission_number || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="border-top pt-3 text-muted small">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Grade / Level:</span>
                      <strong className="text-dark">{selectedStudentProfile?.grade || "Standard"}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Contact Mobile:</span>
                      <strong className="text-dark">{selectedStudentProfile?.mobile_no || "N/A"}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Status:</span>
                      <span className="badge bg-success-light text-success fw-700 uppercase">
                        {selectedStudentProfile?.status || "Active"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ATTENDANCE & HOMEWORK STATS METRIC GRID */}
              <div className="col-12 col-md-8">
                <div className="mnc-card p-3 p-md-4 h-100 border bg-white">
                  <h6 className="fw-800 text-dark mb-3 uppercase tracking-wider small">Overall Performance Metrics</h6>
                  
                  <div className="row g-2">
                    {/* ATTENDANCE METRICS */}
                    <div className="col-6 col-sm-3">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">Total Sessions</span>
                        <h4 className="fw-800 text-dark mt-1 mb-0">{summaryStats?.total_sessions || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">Att. Present</span>
                        <h4 className="fw-800 text-success mt-1 mb-0">{summaryStats?.total_present || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">Att. Absent</span>
                        <h4 className="fw-800 text-danger mt-1 mb-0">{summaryStats?.total_absent || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">Attendance Rate</span>
                        <h4 className="fw-800 text-primary mt-1 mb-0">{summaryStats?.attendance_percentage ?? 0}%</h4>
                      </div>
                    </div>

                    {/* HOMEWORK METRICS */}
                    <div className="col-6 col-sm-3 mt-2">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">Assigned HW</span>
                        <h4 className="fw-800 text-dark mt-1 mb-0">{summaryStats?.total_homework || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3 mt-2">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">HW Pending</span>
                        <h4 className="fw-800 text-danger mt-1 mb-0">{summaryStats?.homework_pending || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3 mt-2">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">HW Submitted</span>
                        <h4 className="fw-800 text-warning-dark mt-1 mb-0">{summaryStats?.homework_submitted || 0}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3 mt-2">
                      <div className="stat-card border bg-light p-2 text-center rounded-12">
                        <span className="stat-label">HW Rate</span>
                        <h4 className="fw-800 text-success mt-1 mb-0">{summaryStats?.homework_completion_rate ?? 0}%</h4>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>

            {/* SEGMENTED TAB SWITCHER */}
            <div className="d-flex gap-2 mb-3">
              <button
                type="button"
                className={`btn btn-sm px-4 rounded-pill fw-700 ${activeTab === "attendance" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("attendance")}
              >
                📅 Session Attendance ({sessions.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm px-4 rounded-pill fw-700 ${activeTab === "homework" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("homework")}
              >
                📝 Homework Status ({homeworkList.length})
              </button>
            </div>

            {/* TAB 1: SESSION-BY-SESSION ATTENDANCE TABLE */}
            {activeTab === "attendance" && (
              <div className="mnc-card shadow-sm border bg-white mb-4">
                <div className="mnc-card-header p-3 p-md-4 border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="fw-800 text-dark mb-0">Session History &amp; Topic Logs</h5>
                  <span className="badge bg-light text-muted fw-600">
                    {sessions.length} Session{sessions.length !== 1 ? "s" : ""} Recorded
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table mnc-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Date &amp; Time</th>
                        <th>Batch / Session Name</th>
                        <th>Topic Covered</th>
                        <th className="text-center">Mode</th>
                        <th className="text-center">Attendance Status</th>
                        <th>Notes / Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">
                            No session attendance logs found for the selected date range.
                          </td>
                        </tr>
                      ) : (
                        sessions.map((session, idx) => {
                          const statusText = session.attendance_status || session.status || "Absent";
                          const isPresent = String(statusText).trim().toLowerCase() === "present";

                          return (
                            <tr key={session.attendance_id || session.session_info_id || idx}>
                              <td className="fw-700 text-muted">{idx + 1}</td>
                              <td className="fw-600 text-dark">
                                <div>{session.session_date || session.date || "N/A"}</div>
                                <small className="text-muted">
                                  {session.start_time ? `${session.start_time} - ${session.end_time}` : ""}
                                </small>
                              </td>
                              <td>
                                <div className="fw-700 text-dark">{session.session_name || "Regular Batch"}</div>
                                <small className="text-muted">{session.category || ""}</small>
                              </td>
                              <td className="fw-600 text-dark">
                                {session.topic || "General Session"}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark uppercase fw-700 border">
                                  {session.mode || "Online"}
                                </span>
                              </td>
                              <td className="text-center">
                                {isPresent ? (
                                  <span className="badge-status bg-success-light text-success">Present</span>
                                ) : (
                                  <span className="badge-status bg-danger-light text-danger">Absent</span>
                                )}
                              </td>
                              <td className="small text-muted" style={{ maxWidth: 300 }}>
                                {session.notes || session.remarks || "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 2: HOMEWORK ASSIGNMENT TRACKER TABLE */}
            {activeTab === "homework" && (
              <div className="mnc-card shadow-sm border bg-white mb-4">
                <div className="mnc-card-header p-3 p-md-4 border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="fw-800 text-dark mb-0">Homework Assignments &amp; Submission Logs</h5>
                  <span className="badge bg-light text-muted fw-600">
                    {homeworkList.length} Assignment{homeworkList.length !== 1 ? "s" : ""} Found
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table mnc-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Subject &amp; Title</th>
                        <th>Due Date</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Status</th>
                        <th>Student Notes / Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {homeworkList.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            No homework assignments found for this student.
                          </td>
                        </tr>
                      ) : (
                        homeworkList.map((hw, idx) => {
                          const status = String(hw.submission_status || "Pending").toLowerCase();

                          return (
                            <tr key={hw.homework_assignment_id || idx}>
                              <td className="fw-700 text-muted">{idx + 1}</td>
                              <td>
                                <div className="fw-700 text-dark">{hw.title}</div>
                                <small className="text-muted">{hw.subject} {hw.external_link ? `· [Link]` : ""}</small>
                              </td>
                              <td className="fw-600 text-dark">
                                {hw.due_date ? new Date(hw.due_date).toLocaleDateString("en-IN") : "N/A"}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark uppercase fw-700 border">
                                  {hw.assignment_type || "Session"}
                                </span>
                              </td>
                              <td className="text-center">
                                {status === "reviewed" ? (
                                  <span className="badge-status bg-success-light text-success">Reviewed</span>
                                ) : status === "submitted" ? (
                                  <span className="badge-status bg-warning-light text-warning-dark">Submitted</span>
                                ) : (
                                  <span className="badge-status bg-danger-light text-danger">Pending</span>
                                )}
                              </td>
                              <td className="small text-muted" style={{ maxWidth: 280 }}>
                                {hw.student_notes && (
                                  <div><strong>Student:</strong> {hw.student_notes}</div>
                                )}
                                {hw.teacher_feedback && (
                                  <div className="text-primary mt-1"><strong>Feedback:</strong> {hw.teacher_feedback}</div>
                                )}
                                {!hw.student_notes && !hw.teacher_feedback && "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}

        {/* =====================================================
            SCENARIO B: ALL STUDENTS AGGREGATE SUMMARY REPORT
        ===================================================== */}
        {!loading && !isSingleStudent && reportType === "all_students_summary" && (
          <div className="mnc-card shadow-sm border bg-white">
            <div className="mnc-card-header p-3 p-md-4 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h5 className="fw-800 text-dark mb-0">Active Students Performance &amp; Homework Summary</h5>
                <small className="text-muted">Showing aggregate attendance and homework completion across all students</small>
              </div>
              <span className="badge bg-light text-dark fw-700">Total: {total} Students</span>
            </div>

            <div className="table-responsive">
              <table className="table mnc-table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th>Student Name</th>
                    <th>Admission No</th>
                    <th className="text-center">Att. Sessions</th>
                    <th className="text-center">Att. %</th>
                    <th className="text-center">HW Total</th>
                    <th className="text-center">HW Pending</th>
                    <th className="text-center">HW Submitted</th>
                    <th className="text-center">HW %</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {studentsSummary.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5 text-muted">
                        No student report data available.
                      </td>
                    </tr>
                  ) : (
                    studentsSummary.map((item, idx) => {
                      const rowNum = (currentPage - 1) * limit + idx + 1;
                      const attPercentage = Number(item.attendance_percentage || 0);
                      const hwPercentage = Number(item.homework_completion_rate || 0);

                      return (
                        <tr key={item.student_id || idx}>
                          <td className="fw-700 text-muted">{rowNum}</td>
                          <td>
                            <div className="fw-700 text-dark">{item.student_name}</div>
                            <small className="text-muted">{item.mobile_no || "Active"}</small>
                          </td>
                          <td className="fw-600 text-secondary">{item.admission_number || "—"}</td>
                          
                          {/* ATTENDANCE AGGREGATES */}
                          <td className="text-center fw-700">{item.total_sessions || 0}</td>
                          <td className="text-center">
                            <span
                              className={`badge-status ${
                                attPercentage >= 75
                                  ? "bg-success-light text-success"
                                  : attPercentage >= 50
                                  ? "bg-warning-light text-warning-dark"
                                  : "bg-danger-light text-danger"
                              }`}
                            >
                              {attPercentage}%
                            </span>
                          </td>

                          {/* HOMEWORK AGGREGATES */}
                          <td className="text-center fw-700">{item.total_homework || 0}</td>
                          <td className="text-center fw-700 text-danger">{item.homework_pending || 0}</td>
                          <td className="text-center fw-700 text-success">
                            {(item.homework_submitted || 0) + (item.homework_reviewed || 0)}
                          </td>
                          <td className="text-center">
                            <span
                              className={`badge-status ${
                                hwPercentage >= 75
                                  ? "bg-success-light text-success"
                                  : hwPercentage >= 50
                                  ? "bg-warning-light text-warning-dark"
                                  : "bg-danger-light text-danger"
                              }`}
                            >
                              {hwPercentage}%
                            </span>
                          </td>

                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary rounded-pill fw-700 px-3"
                              onClick={() => handleStudentSelect(String(item.student_id))}
                            >
                              View Details →
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION FOOTER */}
            {totalPages > 1 && (
              <div className="p-3 border-top d-flex justify-content-between align-items-center bg-light mnc-pagination">
                <span className="small text-muted fw-600">
                  Page {currentPage} of {totalPages} ({total} Records)
                </span>
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-secondary fw-700"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary fw-700"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* STYLES — Apple-inspired design system */}
      <style>{`
        :root {
          --mnc-bg: #f5f5f7;
          --mnc-surface: #ffffff;
          --mnc-ink: #1d1d1f;
          --mnc-ink-soft: #424245;
          --mnc-muted: #86868b;
          --mnc-hairline: #d2d2d7;
          --mnc-blue: #0071e3;
          --mnc-blue-dark: #0058b0;
          --mnc-blue-tint: #e8f1fd;
          --mnc-green: #1d8a4a;
          --mnc-green-tint: #e6f7ed;
          --mnc-amber: #b9770e;
          --mnc-amber-tint: #fdf1dc;
          --mnc-red: #d70015;
          --mnc-red-tint: #fdeaec;
          --mnc-radius-lg: 18px;
          --mnc-radius-md: 12px;
          --mnc-radius-sm: 8px;
          --mnc-font: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
            "Helvetica Neue", Helvetica, Arial, sans-serif;
        }

        * { -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }

        .mnc-page-bg {
          background: var(--mnc-bg);
          min-height: 100vh;
          font-family: var(--mnc-font);
          color: var(--mnc-ink);
          letter-spacing: -0.01em;
        }

        .max-w-1400 { max-width: 1400px; margin: 0 auto; }
        .fw-800 { font-weight: 700; }
        .fw-700 { font-weight: 600; }
        .fw-600 { font-weight: 500; }
        .uppercase { text-transform: uppercase; }

        .text-dark { color: var(--mnc-ink) !important; }
        .text-muted { color: var(--mnc-muted) !important; }
        .text-secondary { color: var(--mnc-ink-soft) !important; }
        .text-primary { color: var(--mnc-blue) !important; }
        .text-success { color: var(--mnc-green) !important; }
        .text-danger { color: var(--mnc-red) !important; }
        .text-warning-dark { color: var(--mnc-amber) !important; }

        h4, h5, h3, h6 { letter-spacing: -0.02em; }
        h4.fw-800 { font-size: 1.5rem; }
        h5.fw-800 { font-size: 1.05rem; }

        .badge-mnc {
          font-size: 0.68rem;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 100px;
          letter-spacing: 0.02em;
        }
        .bg-primary-light { background: var(--mnc-blue-tint); color: var(--mnc-blue-dark); }
        .bg-success-light { background: var(--mnc-green-tint); color: var(--mnc-green); }
        .bg-warning-light { background: var(--mnc-amber-tint); color: var(--mnc-amber); }
        .bg-danger-light { background: var(--mnc-red-tint); color: var(--mnc-red); }
        .bg-light { background: #f5f5f7 !important; }
        .bg-white { background: var(--mnc-surface) !important; }

        .mnc-card {
          background: var(--mnc-surface);
          border-radius: var(--mnc-radius-lg);
          overflow: visible !important;
          border-color: var(--mnc-hairline) !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px -12px rgba(0, 0, 0, 0.08);
        }
        .border { border: 1px solid var(--mnc-hairline) !important; }
        .border-top { border-top: 1px solid var(--mnc-hairline) !important; }
        .border-bottom { border-bottom: 1px solid var(--mnc-hairline) !important; }

        .rounded-12 { border-radius: var(--mnc-radius-md); }

        .avatar-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.15rem;
          flex-shrink: 0;
        }

        .stat-card {
          border-color: var(--mnc-hairline) !important;
          border-radius: var(--mnc-radius-md);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .stat-label {
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--mnc-muted);
          letter-spacing: 0.03em;
        }
        .stat-card h4 { font-size: 1.25rem; }

        .mnc-label {
          display: block;
          margin-bottom: 6px;
          font-size: .72rem;
          font-weight: 600;
          color: var(--mnc-ink-soft);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .mnc-field {
          width: 100%;
          border: 1px solid var(--mnc-hairline);
          border-radius: var(--mnc-radius-sm);
          padding: 11px 14px;
          font-size: 0.95rem;
          font-family: var(--mnc-font);
          color: var(--mnc-ink);
          background-color: var(--mnc-surface);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          min-height: 46px;
        }
        .mnc-field:focus {
          outline: none;
          border-color: var(--mnc-blue);
          box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.14);
        }

        .btn-mnc-sync {
          background: var(--mnc-blue);
          color: #fff;
          border: none;
          border-radius: var(--mnc-radius-sm);
          padding: 12px 24px;
          font-weight: 600;
          font-size: 0.92rem;
          font-family: var(--mnc-font);
          transition: background-color 0.15s ease, transform 0.05s ease;
          min-height: 46px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .btn-mnc-sync:hover:not(:disabled) { background: var(--mnc-blue-dark); }
        .btn-mnc-sync:active:not(:disabled) { transform: scale(0.98); }
        .btn-mnc-sync:disabled { opacity: 0.6; }

        .btn-mnc-discard {
          background: var(--mnc-surface);
          border: 1px solid var(--mnc-hairline);
          color: var(--mnc-ink-soft);
          border-radius: var(--mnc-radius-sm);
          padding: 12px 20px;
          font-weight: 600;
          font-size: 0.92rem;
          font-family: var(--mnc-font);
          min-height: 46px;
          white-space: nowrap;
        }
        .btn-mnc-discard:hover:not(:disabled) { background: #f5f5f7; }

        .btn-outline-secondary {
          border-color: var(--mnc-hairline) !important;
          color: var(--mnc-ink-soft) !important;
          font-family: var(--mnc-font);
        }
        .btn-outline-secondary:hover:not(:disabled) { background: #f5f5f7 !important; }
        .btn-outline-primary {
          border-color: var(--mnc-blue) !important;
          color: var(--mnc-blue) !important;
          font-family: var(--mnc-font);
        }
        .btn-outline-primary:hover { background: var(--mnc-blue) !important; color: #fff !important; }
        .btn-dark {
          background: var(--mnc-ink) !important;
          border-color: var(--mnc-ink) !important;
          font-family: var(--mnc-font);
        }
        .btn-dark:hover:not(:disabled) { background: #000 !important; }

        /* Table Styling */
        .mnc-table th {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--mnc-ink-soft);
          background-color: #fafafa;
          padding: 14px 16px;
          border-bottom: 1px solid var(--mnc-hairline);
          white-space: nowrap;
        }
        .mnc-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #f0f0f2;
          font-size: 0.9rem;
        }
        .mnc-table tbody tr:hover { background: #fafafa; }

        .badge-status {
          font-size: 0.74rem;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 100px;
          display: inline-block;
        }

        .badge.bg-light {
          border: 1px solid var(--mnc-hairline) !important;
          font-weight: 500;
          border-radius: 100px;
          padding: 5px 10px;
        }

        /* ===== SEARCHABLE COMBOBOX & DROPDOWN MENU ===== */
        .mnc-combobox {
          position: relative;
          width: 100%;
        }
        .mnc-combobox-field {
          position: relative;
          display: flex;
          align-items: center;
          cursor: pointer;
          border: 1px solid var(--mnc-hairline);
          border-radius: var(--mnc-radius-sm);
          background: var(--mnc-surface);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .mnc-combobox-field .mnc-field {
          border: none;
          box-shadow: none !important;
          min-height: 44px;
        }
        .mnc-combobox-icon {
          position: absolute;
          left: 14px;
          color: var(--mnc-muted);
          pointer-events: none;
          z-index: 1;
        }
        .mnc-combobox-input {
          padding-left: 38px;
          padding-right: 64px;
          background: transparent;
        }
        .mnc-combobox-field.is-open {
          border-color: var(--mnc-blue);
          box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.14);
        }
        .mnc-combobox-field.is-disabled {
          background: #f5f5f7;
          cursor: not-allowed;
        }
        .mnc-combobox-controls {
          position: absolute;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .mnc-combobox-clear {
          width: 24px;
          height: 24px;
          border: none;
          background: #f0f0f2;
          color: var(--mnc-ink-soft);
          border-radius: 50%;
          font-size: 1rem;
          line-height: 1;
          cursor: pointer;
          flex-shrink: 0;
        }
        .mnc-combobox-clear:hover { background: var(--mnc-hairline); }
        .mnc-combobox-chevron {
          color: var(--mnc-muted);
          pointer-events: none;
          transition: transform 0.15s ease;
          flex-shrink: 0;
        }
        .mnc-combobox-chevron.is-open {
          transform: rotate(180deg);
          color: var(--mnc-blue);
        }

        /* High-Priority Dropdown Menu Popup Overlay */
        .mnc-combobox-list {
          position: absolute;
          z-index: 9999 !important;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          max-height: 280px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          background: var(--mnc-surface);
          border: 1px solid var(--mnc-hairline);
          border-radius: var(--mnc-radius-md);
          box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.22);
          margin: 0;
          padding: 6px;
          list-style: none;
        }

        .mnc-combobox-list::-webkit-scrollbar {
          width: 6px;
        }
        .mnc-combobox-list::-webkit-scrollbar-track {
          background: #f5f5f7;
          border-radius: 10px;
        }
        .mnc-combobox-list::-webkit-scrollbar-thumb {
          background: #c1c1c7;
          border-radius: 10px;
        }
        .mnc-combobox-list::-webkit-scrollbar-thumb:hover {
          background: #a1a1a8;
        }

        .mnc-combobox-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: var(--mnc-radius-sm);
          cursor: pointer;
          font-size: 0.9rem;
          color: var(--mnc-ink);
          user-select: none;
        }
        .mnc-combobox-option-text {
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex: 1 1 auto;
        }
        .mnc-combobox-option-name {
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mnc-combobox-option-sub { font-size: 0.76rem; color: var(--mnc-muted); font-weight: 500; }
        .mnc-combobox-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 700;
          background: var(--mnc-blue-tint);
          color: var(--mnc-blue-dark);
        }
        .mnc-combobox-avatar-overview { background: #eef0ff; color: #4b3fdb; font-size: 0.95rem; }
        .mnc-combobox-check { color: var(--mnc-blue); flex-shrink: 0; }
        .mnc-combobox-divider {
          height: 1px;
          background: var(--mnc-hairline);
          margin: 6px 4px;
          list-style: none;
        }
        .mnc-combobox-option.is-active { background: var(--mnc-blue-tint); }
        .mnc-combobox-option.is-selected { background: #fafafa; }
        .mnc-combobox-option.is-selected .mnc-combobox-option-name { color: var(--mnc-blue-dark); }
        .mnc-combobox-empty { padding: 16px 12px; font-size: 0.86rem; color: var(--mnc-muted); text-align: center; }

        .mnc-selected-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--mnc-blue-dark);
          background: var(--mnc-blue-tint);
          padding: 6px 12px;
          border-radius: 100px;
        }
        .mnc-selected-chip-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--mnc-blue);
        }

        /* Mobile Responsiveness */
        @media (max-width: 767.98px) {
          .mnc-page-bg { padding-left: 0; padding-right: 0; }
          h4.fw-800 { font-size: 1.25rem; }

          .mnc-card-header .d-flex.flex-wrap { align-items: flex-start; }
          .mnc-header-actions {
            width: 100%;
            flex-wrap: wrap;
          }
          .mnc-header-actions button { flex: 1 1 auto; }

          .mnc-action-buttons { flex-wrap: wrap; }
          .mnc-action-buttons .btn-mnc-sync,
          .mnc-action-buttons .btn-mnc-discard {
            width: 100%;
          }

          .mnc-field, .mnc-combobox-input { font-size: 16px; }

          .mnc-table th, .mnc-table td { padding: 12px 10px; font-size: 0.84rem; }
          .stat-card h4 { font-size: 1.1rem; }

          .mnc-pagination {
            flex-direction: column;
            align-items: stretch !important;
            gap: 10px;
          }
          .mnc-pagination .btn-group { display: flex; }
          .mnc-pagination .btn-group .btn { flex: 1 1 50%; }
        }

        @media (max-width: 575.98px) {
          .mnc-card { border-radius: 14px; }
          .avatar-circle { width: 42px; height: 42px; font-size: 1rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default StudentReports;